import { boundMethod } from 'autobind-decorator';
import {
  AsyncStatus,
  Queue,
  QueuePayload,
  QueueResponse,
} from 'lib-shared-legacy-types';
import isEqual from 'lodash/isEqual';
import memoize from 'memoize-one';
import { ComponentType, PureComponent } from 'react';

import { ajaxSend } from 'api/api';

import {
  createQueue,
  getQueue,
  getQueues,
  QueuesGetItemsResponse,
  updateQueue,
} from './api/queues';

const DEFAULT_FETCH_THROTTLE = 300;

export interface WithQueuesInjectedProps {
  queues: {
    /** Loaded queues of current page */
    items: Array<Queue>;

    /** Loaded queue with a specific id */
    item: Queue | null;

    /** AsyncStatus of get queues api */
    getItemsAsyncStatus: AsyncStatus;

    /** AsyncStatus of create queue api */
    createAsyncStatus: AsyncStatus;

    /** AsyncStatus of update queue api */
    updateAsyncStatus: AsyncStatus;

    /** AsyncStatus of get queue api */
    getItemAsyncStatus: AsyncStatus;

    /** The search term used in get queues api */
    searchTerm: string;

    /** The sorting option used in get queues api */
    sortBy: string;

    /** Whether there is next page or not */
    hasNextPage: boolean;

    /** Whether there is previous page or not */
    hasPreviousPage: boolean;

    /**
     * Action to get queues. It will use the current search term to filter
     * queues.
     */
    getItems: () => Promise<QueuesGetItemsResponse>;

    /**
     * Handle search term change.
     *
     * @param {string} searchTerm - The search term
     * @param {number} [fetchThrottle=300] - If > 0 it will re-fetch queues
     *   after given milliseconds. Default is `300`
     */
    searchTermChange: (
      searchTerm?: string,
      fetchThrottle?: number,
    ) => Promise<QueuesGetItemsResponse | void>;

    /**
     * Handle sorting change.
     *
     * @param {string} sortBy - Column to sort the list by
     * @param {string} direction - "asc" or "desc", if missing the default is
     *   "asc" on the backend
     */
    sortByChange: (
      sortBy?: string,
      direction?: string,
    ) => Promise<QueuesGetItemsResponse>;

    /** Get queues of the next page */
    nextPage: () => Promise<QueuesGetItemsResponse>;

    /** Get queues of the previous page */
    previousPage: () => Promise<QueuesGetItemsResponse>;

    /**
     * Get a specific queue
     *
     * @param {queueId} - The id of the queue
     */
    getQueue: (queueId: string) => Promise<QueueResponse>;

    /**
     * Create a new queue
     *
     * @param {Queue} - The queue to be created
     */
    createQueue: (queue: QueuePayload) => Promise<QueueResponse>;

    /**
     * Update a queue
     *
     * @param {string} queueId - Id of the queue to be updated. Currently it is
     *   the name that works as id
     * @param {Queue} queue - Updated queue data
     */
    updateQueue: (
      queueId: string,
      queue: QueuePayload,
    ) => Promise<QueueResponse>;
  };
}

type WithQueuesNotInjectedProps<T> = Omit<T, keyof WithQueuesInjectedProps>;

type WithQueuesProps<T> = WithQueuesNotInjectedProps<T>;

/** The position of the new data, used when `settings.infinite` is `true` */
enum NewDataPosition {
  PreviousPage = -1,
  Replace = 0,
  NextPage = 1,
}

interface WithQueuesState {
  searchTerm: string;
  sortBy: string;
  items: Array<Queue>;
  item: Queue | null;
  nextPagePath: string | null;
  previousPagePath: string | null;
  getItemsAsyncStatus: AsyncStatus;
  createAsyncStatus: AsyncStatus;
  updateAsyncStatus: AsyncStatus;
  getItemAsyncStatus: AsyncStatus;
}

interface WithQueuesSettings {
  pageSize?: number;
  infinite?: boolean;
}

export const withQueues = <T extends WithQueuesInjectedProps>(
  Component: ComponentType<T>,
  settings: WithQueuesSettings = { infinite: false },
) => {
  class WithQueues extends PureComponent<WithQueuesProps<T>, WithQueuesState> {
    timeout: ReturnType<typeof setTimeout> | null;

    constructor(props: WithQueuesProps<T>) {
      super(props);

      this.timeout = null;
      this.state = {
        items: [],
        item: null,
        searchTerm: '',
        sortBy: 'name:asc',
        nextPagePath: null,
        previousPagePath: null,
        getItemsAsyncStatus: 'succeeded',
        createAsyncStatus: 'succeeded',
        updateAsyncStatus: 'succeeded',
        getItemAsyncStatus: 'succeeded',
      };
      this.memoizedGetInjectedProps = memoize(
        this.memoizedGetInjectedProps,
        isEqual,
      ).bind(this); // We have to bind it like this. Using @boundMethod won't work
    }

    componentWillUnmount() {
      if (this.timeout) {
        clearTimeout(this.timeout);
      }
    }

    @boundMethod
    getItems(
      position: NewDataPosition = NewDataPosition.Replace,
    ): Promise<QueuesGetItemsResponse> {
      this.setState({ getItemsAsyncStatus: 'loading' });

      return getQueues({
        search: this.state.searchTerm,
        limit: settings.pageSize,
        sort_by: !this.state.searchTerm ? this.state.sortBy : '', // We don't support sort and search at the same time
        is_archived: false,
      })
        .then((resp) => this.handleQueryResolve(resp, position))
        .catch(this.handleQueryReject);
    }

    @boundMethod
    getItemsWithPath(
      path: string,
      position: NewDataPosition,
    ): Promise<QueuesGetItemsResponse> {
      this.setState({ getItemsAsyncStatus: 'loading' });

      return ajaxSend<QueuesGetItemsResponse>({ path })
        .then((resp) => this.handleQueryResolve(resp, position))
        .catch(this.handleQueryReject);
    }

    @boundMethod
    nextPage(): Promise<QueuesGetItemsResponse> | never {
      const { nextPagePath } = this.state;
      if (nextPagePath) {
        return this.getItemsWithPath(nextPagePath, NewDataPosition.NextPage);
      }
      throw new Error('Next page does not exist');
    }

    @boundMethod
    previousPage(): Promise<QueuesGetItemsResponse> | never {
      const { previousPagePath } = this.state;
      if (previousPagePath) {
        return this.getItemsWithPath(
          previousPagePath,
          NewDataPosition.PreviousPage,
        );
      }
      throw new Error('Previous page does not exist');
    }

    @boundMethod
    getQueue(queueId: string): Promise<QueueResponse> {
      this.setState({ getItemAsyncStatus: 'loading' });
      return getQueue(queueId)
        .then((response) => {
          this.setState({
            getItemAsyncStatus: 'succeeded',
            item: response.data,
          });
          return response;
        })
        .catch((err) => {
          this.setState({ getItemAsyncStatus: 'failed' });
          throw err;
        });
    }

    @boundMethod
    createQueue(queue: QueuePayload): Promise<QueueResponse> {
      this.setState({ createAsyncStatus: 'loading' });
      return createQueue(queue)
        .then((response) => {
          this.setState({ createAsyncStatus: 'succeeded' });
          return response;
        })
        .catch((err) => {
          this.setState({ createAsyncStatus: 'failed' });
          throw err;
        });
    }

    @boundMethod
    updateQueue(queueId: string, queue: QueuePayload): Promise<QueueResponse> {
      this.setState({ updateAsyncStatus: 'loading' });
      return updateQueue(queueId, queue)
        .then((response) => {
          this.setState({ updateAsyncStatus: 'succeeded' });
          return response;
        })
        .catch((err) => {
          this.setState({ updateAsyncStatus: 'failed' });
          throw err;
        });
    }

    @boundMethod
    handleSearchTermChange(
      searchTerm = '',
      fetchThrottle: number = DEFAULT_FETCH_THROTTLE,
    ) {
      return new Promise<QueuesGetItemsResponse | void>((resolve) => {
        this.setState({ searchTerm }, () => {
          if (fetchThrottle >= 0) {
            if (this.timeout) {
              clearTimeout(this.timeout);
            }

            this.timeout = setTimeout(() => {
              resolve(this.getItems());
            }, fetchThrottle);
          } else {
            resolve();
          }
        });
      });
    }

    @boundMethod
    handleSortByChange(sortBy = '', direction = 'asc') {
      return new Promise<QueuesGetItemsResponse>((resolve) => {
        this.setState(
          {
            sortBy: `${sortBy}:${direction}`,
          },
          () => {
            resolve(this.getItems());
          },
        );
      });
    }

    @boundMethod
    handleQueryResolve(
      response: QueuesGetItemsResponse,
      position: NewDataPosition = NewDataPosition.Replace,
    ) {
      this.setState({
        getItemsAsyncStatus: 'succeeded',
        items: this.getPaginatedItems(
          response.data.queues,
          this.state.items,
          position,
        ),
        nextPagePath: response.meta.next_page,
        previousPagePath: response.meta.prev_page,
      });

      return response;
    }

    @boundMethod
    handleQueryReject(error) {
      this.setState({
        getItemsAsyncStatus: 'failed',
      });

      return error;
    }

    getPaginatedItems(
      newItems: Array<Queue>,
      oldItems: Array<Queue>,
      position: NewDataPosition = NewDataPosition.Replace,
    ): Array<Queue> {
      if (!settings.infinite || position === NewDataPosition.Replace) {
        return newItems;
      }

      if (position === NewDataPosition.NextPage) {
        return oldItems.concat(newItems);
      }

      if (position === NewDataPosition.PreviousPage) {
        return newItems.concat(oldItems);
      }

      return [];
    }

    @boundMethod
    getInjectedProps(
      props: Readonly<WithQueuesProps<T>>,
      state: Readonly<WithQueuesState>,
    ): WithQueuesInjectedProps['queues'] {
      const {
        items,
        item,
        searchTerm,
        sortBy,
        nextPagePath,
        previousPagePath,
        getItemsAsyncStatus,
        createAsyncStatus,
        updateAsyncStatus,
        getItemAsyncStatus,
      } = state;

      return this.memoizedGetInjectedProps(
        searchTerm,
        sortBy,
        items,
        item,
        getItemsAsyncStatus,
        createAsyncStatus,
        updateAsyncStatus,
        getItemAsyncStatus,
        nextPagePath,
        previousPagePath,
      );
    }

    memoizedGetInjectedProps(
      searchTerm: Readonly<WithQueuesState>['searchTerm'],
      sortBy: Readonly<WithQueuesState>['sortBy'],
      items: Readonly<WithQueuesState>['items'],
      item: Readonly<WithQueuesState>['item'],
      getItemsAsyncStatus: Readonly<WithQueuesState>['getItemsAsyncStatus'],
      createAsyncStatus: Readonly<WithQueuesState>['createAsyncStatus'],
      updateAsyncStatus: Readonly<WithQueuesState>['updateAsyncStatus'],
      getItemAsyncStatus: Readonly<WithQueuesState>['getItemAsyncStatus'],
      nextPagePath: Readonly<WithQueuesState>['nextPagePath'],
      previousPagePath: Readonly<WithQueuesState>['previousPagePath'],
    ): WithQueuesInjectedProps['queues'] {
      return {
        searchTerm: searchTerm,
        sortBy: sortBy,
        items: items,
        item: item,
        getItemsAsyncStatus: getItemsAsyncStatus,
        createAsyncStatus: createAsyncStatus,
        updateAsyncStatus: updateAsyncStatus,
        getItemAsyncStatus: getItemAsyncStatus,
        hasNextPage: nextPagePath !== null,
        hasPreviousPage: previousPagePath !== null,
        getItems: this.getItems,
        searchTermChange: this.handleSearchTermChange,
        sortByChange: this.handleSortByChange,
        nextPage: this.nextPage,
        previousPage: this.previousPage,
        createQueue: this.createQueue,
        updateQueue: this.updateQueue,
        getQueue: this.getQueue,
      };
    }

    render() {
      return (
        <Component
          {...(this.props as T)}
          queues={this.getInjectedProps(this.props, this.state)}
        />
      );
    }
  }

  return WithQueues;
};
